import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroll-component'

import { Avatar } from 'antd'
import { TbLoader2 } from 'react-icons/tb'

import {
    changePage,
    getJobApplicantListByStatus,
    jobApplicantState,
} from '../store/jobApplicant.slice'
import { updateUserGet } from '../../profile/profile.slice'

import styles from '../jobApplications.module.css'

const LIST_SCROLL_ID = 'job-applicants-scroll'

const TalentTag = () => <span className={styles.tagTalent}>Talent</span>

const NewTag = () => <span className={styles.tagNew}>New</span>

const JobApplicationList = ({ state, setState }) => {
    const dispatch = useDispatch()
    const params = useParams()
    const fetchingMoreRef = useRef(false)
    const prevListLengthRef = useRef(0)

    const [applicationsList, setApplicationsList] = useState([])

    const {
        applicantList,
        applicantIsNext,
        applicantPageLimit,
        loaders,
    } = useSelector(jobApplicantState)

    useEffect(() => {
        prevListLengthRef.current = 0
    }, [state?.jobStatus, params.id])

    useEffect(() => {
        const list = applicantList || []
        const prevLength = prevListLengthRef.current
        const isAppend = list.length > prevLength && prevLength > 0

        setApplicationsList(list)
        prevListLengthRef.current = list.length

        // Only auto-select the first applicant on a fresh list load
        if (!isAppend && list.length > 0) {
            setState((prevState) => ({
                ...prevState,
                selectedApplicantData: list[0],
                selectedApplicantIndex: 0,
            }))

            if (list[0]?.candidateId) {
                dispatch(
                    updateUserGet({
                        _id: list[0]?.candidateId,
                    })
                )
            }
        }

        if (list.length === 0) {
            setState((prevState) => ({
                ...prevState,
                selectedApplicantData: null,
                selectedApplicantIndex: null,
            }))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicantList])

    const fetchMoreApplicants = useCallback(() => {
        if (
            !applicantIsNext ||
            loaders?.applicantLoading ||
            loaders?.applicantLoadingMore ||
            fetchingMoreRef.current
        ) {
            return
        }

        fetchingMoreRef.current = true
        const nextSkip = applicantList?.length || 0
        const pageLimit = applicantPageLimit || 15

        dispatch(
            changePage({
                currentPage: Math.floor(nextSkip / pageLimit) + 1,
                skip: nextSkip,
                pageLimit,
            })
        )

        dispatch(
            getJobApplicantListByStatus({
                skip: nextSkip,
                limit: pageLimit,
                filter: {
                    status: state?.jobStatus || 'matches',
                    job: params.id,
                },
            })
        ).finally(() => {
            fetchingMoreRef.current = false
        })
    }, [
        applicantIsNext,
        loaders?.applicantLoading,
        loaders?.applicantLoadingMore,
        applicantList?.length,
        applicantPageLimit,
        dispatch,
        state?.jobStatus,
        params.id,
    ])

    // If first page fits without overflow, keep loading until scrollable or no more
    useEffect(() => {
        if (
            !applicantIsNext ||
            loaders?.applicantLoading ||
            loaders?.applicantLoadingMore ||
            !applicationsList.length
        ) {
            return
        }

        const el = document.getElementById(LIST_SCROLL_ID)
        if (!el) return

        if (el.scrollHeight <= el.clientHeight + 4) {
            fetchMoreApplicants()
        }
    }, [
        applicationsList.length,
        applicantIsNext,
        loaders?.applicantLoading,
        loaders?.applicantLoadingMore,
        fetchMoreApplicants,
    ])

    const handleSelectApplicant = (application, index) => {
        setState((prevState) => ({
            ...prevState,
            selectedApplicantData: application,
            selectedApplicantIndex: index,
        }))

        dispatch(
            updateUserGet({
                _id: application?.candidateId,
            })
        )
    }

    return (
        <div id={LIST_SCROLL_ID} className={styles.listRoot}>
            {applicationsList?.length > 0 ? (
                <InfiniteScroll
                    dataLength={applicationsList.length}
                    next={fetchMoreApplicants}
                    hasMore={Boolean(applicantIsNext)}
                    scrollableTarget={LIST_SCROLL_ID}
                    loader={
                        loaders?.applicantLoadingMore ? (
                            <div className={styles.listLoader}>
                                <TbLoader2
                                    size={22}
                                    className="spin-loader-resume"
                                    color="#4f46e5"
                                />
                            </div>
                        ) : null
                    }
                    endMessage={null}
                    style={{ overflow: 'visible' }}
                >
                    {applicationsList.map((application, index) => (
                        <div
                            key={
                                application?._id ||
                                application?.candidateId ||
                                index
                            }
                            onClick={() =>
                                handleSelectApplicant(application, index)
                            }
                            className={`${styles.applicantRow} ${
                                state.selectedApplicantIndex === index
                                    ? styles.applicantRowSelected
                                    : ''
                            }`}
                        >
                            <div className={styles.applicantRowMain}>
                                <span>
                                    {application?.profilePicture ? (
                                        <Avatar
                                            src={application?.profilePicture}
                                            size={40}
                                        />
                                    ) : (
                                        <Avatar
                                            size={40}
                                            className={
                                                styles.applicantAvatarFallback
                                            }
                                        >
                                            {application?.fullName
                                                ? `${application?.fullName[0]?.toUpperCase()}${
                                                      application?.fullName?.split(
                                                          ' '
                                                      )[1] !== undefined
                                                          ? application?.fullName
                                                                ?.split(
                                                                    ' '
                                                                )[1][0]
                                                                ?.toUpperCase()
                                                          : ''
                                                  }`
                                                : ''}
                                        </Avatar>
                                    )}
                                </span>

                                <span className={styles.applicantText}>
                                    <span className={styles.applicantName}>
                                        {application?.fullName || ''}
                                    </span>
                                    <span className={styles.applicantRole}>
                                        {
                                            application?.candidate?.designation
                                                ?.name
                                        }
                                    </span>
                                </span>
                            </div>
                            <div>
                                {!application.isTalentUser ? (
                                    <NewTag />
                                ) : (
                                    <TalentTag />
                                )}
                            </div>
                        </div>
                    ))}
                </InfiniteScroll>
            ) : (
                <div className={styles.listEmpty}>
                    No applicants in this status
                </div>
            )}
        </div>
    )
}

export default JobApplicationList
